import { Injectable } from '@angular/core';
import { VaultApiService } from '@app/core/services/api/vault-api.service';
import { AccountingDocumentType } from '@interfaces/accounting-document-management/responses/accounting-document-status.response';
import { AccountingUploadEligibilityResponse } from '@interfaces/accounting-document-management/responses/accounting-upload-eligibility.response';
import { ClassifiedDocumentUploadResponse } from '@interfaces/accounting-document-management/responses/classified-document-upload.response';
import { DocumentCoverageResponse } from '@interfaces/accounting-document-management/responses/document-coverage.response';
import { DocumentFileLinkResponse } from '@interfaces/accounting-document-management/responses/document-file-link.response';
import { Observable } from 'rxjs';

const BASE = 'accounting-document-management';

/**
 * Nenhuma resposta desta tela é cacheada, então não há cache a invalidar depois das escritas: a
 * cobertura reflete um pipeline assíncrono (fila → extração → De/Para → projeção) e cachear fazia a
 * tela afirmar um estado que o backend já tinha superado. Ver {@link getDocumentCoverage}.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountingDocumentManagementService {
  constructor(private apiService: VaultApiService) {}

  /**
   * Envio ancorado por período. `periodStartMonth`/`periodEndMonth` só valem para as trilhas de
   * demonstração (`Dre`/`Balanco`) e são o **escape manual** da classificação automática — enviar o
   * anual por aqui exige `(1, 12)`; sem intervalo, o backend trata o arquivo como o mês informado.
   */
  uploadDocument(
    companyId: string,
    year: number,
    month: number,
    documentType: AccountingDocumentType,
    file: File,
    periodStartMonth?: number,
    periodEndMonth?: number
  ): Observable<null> {
    const interval =
      periodStartMonth !== undefined && periodEndMonth !== undefined
        ? `?periodStartMonth=${periodStartMonth}&periodEndMonth=${periodEndMonth}`
        : '';

    return this.apiService.post<null>(
      `${BASE}/${companyId}/compliance-status/${year}/${month}/${documentType}/upload${interval}`,
      { file },
      true
    );
  }

  /**
   * Envio **sem período**: a Cog AI lê exercício, intervalo e tipo (DRE/Balanço) do próprio documento.
   * É o que a área de envio por exercício da régua usa. `hintYear` é o exercício em que o usuário
   * soltou o arquivo — pista para a IA, não verdade.
   *
   * Quando a classificação fica ambígua, a resposta volta com `createdDocumentCount: 0` e
   * `needsHumanReview: true`: **nada foi arquivado**, e quem chama deve orientar o envio manual pela
   * rota ancorada por período.
   */
  uploadClassifiedDocument(
    companyId: string,
    file: File,
    hintYear?: number
  ): Observable<ClassifiedDocumentUploadResponse | null> {
    const hint = hintYear !== undefined ? `?hintYear=${hintYear}` : '';

    return this.apiService.post<ClassifiedDocumentUploadResponse>(
      `${BASE}/${companyId}/documents/classified-upload${hint}`,
      { file },
      true
    );
  }

  /**
   * Cobertura documental resolvida da empresa numa única chamada: a janela obrigatória e, para cada
   * tipo de documento contábil, o status e o arquivo considerado por período. Consumida pela grade
   * ano × mês da tela — nunca uma chamada por célula/ano.
   *
   * **Sem cache, de propósito.** Esta resposta reflete um pipeline assíncrono (fila → extração →
   * De/Para → projeção), então cachear em `sessionStorage` fazia a tela afirmar por toda a sessão um
   * estado que o backend já tinha superado: um exercício aparecia "Em processamento / Pendente" muito
   * depois de ter virado "Coberto". O cache só era invalidado por escrita feita **pela própria tela**,
   * e nenhuma etapa do pipeline é uma escrita dessa tela.
   *
   * Não custa chamada extra: `AccountingDocumentManagementStateService.loadCoverage` já tem guarda
   * própria (`coverageLoaded`) contra refetch redundante, e toda escrita chama `loadCoverage(true)`.
   */
  getDocumentCoverage(companyId: string): Observable<DocumentCoverageResponse | null> {
    return this.apiService.get<DocumentCoverageResponse>(`${BASE}/${companyId}/document-coverage`);
  }

  /**
   * Gate de elegibilidade de envio (decisão de produto de 24/08/2026): consultado para decidir se a
   * área de envio de cada exercício aparece, ou se dá lugar ao aviso pedindo para iniciar o
   * onboarding. Não afeta a cobertura em si — a grade continua vindo de {@link getDocumentCoverage}
   * e é carregada independentemente disso, porque é informação útil mesmo sem onboarding.
   */
  getUploadEligibility(companyId: string): Observable<AccountingUploadEligibilityResponse | null> {
    return this.apiService.get<AccountingUploadEligibilityResponse>(
      `${BASE}/${companyId}/upload-eligibility`
    );
  }

  /**
   * Exclui (soft delete) um documento contábil da Central de Documentos. O blob no storage é
   * preservado; o backend recalcula o(s) período(s) de conformidade que o arquivo cobria a partir do
   * arquivo restante mais recente (ou `Pending`, se não sobrar nenhum).
   */
  deleteDocument(companyId: string, extractionId: string): Observable<null> {
    return this.apiService.delete<null>(`${BASE}/${companyId}/documents/${extractionId}`);
  }

  /**
   * Resolve o link temporário de download do arquivo original de um documento. Buscado sob demanda no
   * clique (sem cache) — não fica embutido na cobertura, que traria uma SAS por arquivo a cada
   * carregamento da tela.
   */
  getDocumentFileLink(
    companyId: string,
    extractionId: string
  ): Observable<DocumentFileLinkResponse | null> {
    return this.apiService.get<DocumentFileLinkResponse>(
      `${BASE}/${companyId}/documents/${extractionId}/file`
    );
  }
}
